import {ErrorBoundary} from '@sentry/react';
import {useQuery} from '@tanstack/react-query';
import {createColumnHelper} from '@tanstack/react-table';
import React, {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {MapStatsWithRegion} from '../../../src/map-stats';
import {CurrencyValue, GadmLevel, HarvestYear} from '../../../src/models/interfaces';
import {DbFilterState} from '../../../src/redux/reducers/filters';
import {useApis} from '../apis/ApisContext';
import {AreaHa} from '../components/Area';
import {CropTags} from '../components/CropTags';
import {Currency} from '../components/Currency';
import {DownloadButton} from '../components/DownloadButton';
import {InfinityTable, InfinityTableProps, defaultColumnSizes} from '../components/InfinityTable';
import {RegionLevelSelector, regionLevelLabels, useRegionData} from '../components/RegionLevelSelector';
import SpinningDots from '../components/SpinningDots';
import {YieldTHa} from '../components/Yield';
import {State} from '../redux';
import './ListView.css';

type RegionsTableConditionalColumns = 'gross_premium' | 'net_premium' | 'sum_insured';

const fetchSetSize = 100;
const columnHelper = createColumnHelper<MapStatsWithRegion>();
const getRegionId = (row: MapStatsWithRegion): string => row.region?.name ?? '-';
const getCropId = (row: MapStatsWithRegion): null | string => row.crop_id;
const getHarvestYear = (row: MapStatsWithRegion): null | HarvestYear => row.harvest_year;
const getTotalArea = (row: MapStatsWithRegion): number => row.total_area_ha ?? 0;
const getNumFields = (row: MapStatsWithRegion): string => String(row.num_fields ?? '-');
const getNumSamples = (row: MapStatsWithRegion): string => String(row.num_samples ?? '-');
const getWeightedEstimatedYield = (row: MapStatsWithRegion): null | number => row.weighted_estimated_yield_t_ha;
const getStraightEstimatedYield = (row: MapStatsWithRegion): null | number => row.straight_estimated_yield_t_ha;
const sortValue = (row: MapStatsWithRegion): null | string => row.region_id ?? null;
const getGrossPremium = (row: MapStatsWithRegion): null | CurrencyValue => row.gross_premium ?? null;
const getNetPremium = (row: MapStatsWithRegion): null | CurrencyValue => row.net_premium ?? null;
const getSumInsured = (row: MapStatsWithRegion): null | CurrencyValue => row.sum_insured ?? null;
// Note: The row.region_id alone cannot be used as a unique identifier because it can be associated with multiple rows due to different crop_id and harvest_year values.
// Additionally, in some instances, region_id may be null.
const getRowId = (row: MapStatsWithRegion): string => `${row.region_id}-${row.crop_id}-${row.harvest_year}`;

export const RegionsList: React.FC = () => {
  const apis = useApis();
  const filters = useSelector<State, DbFilterState>(state => state.filters);
  const {t} = apis;
  const [selectedRegionLevel, setSelectedRegionLevel] = useState<GadmLevel>('0');
  const {getRegionsData} = useRegionData();

  const {data: regionsData, isLoading: isRegionsDataLoading} = useQuery(
    ['getRegionsData', filters, selectedRegionLevel],
    () => getRegionsData(filters, selectedRegionLevel),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
    },
  );

  const fetchDataFromApi: InfinityTableProps<MapStatsWithRegion>['fetchData'] = useCallback(
    async ({pageParam}): Promise<MapStatsWithRegion[]> => {
      if (pageParam?.continue_from_id || !regionsData) {
        return [];
      }
      return regionsData.mapStatsRowWithName;
    },
    [regionsData],
  );

  const columns = useMemo(() => {
    const conditionalColsPresence: Record<RegionsTableConditionalColumns, boolean> = {
      gross_premium: false,
      net_premium: false,
      sum_insured: false,
    };

    for (const row of regionsData?.mapStats ?? []) {
      if (row.gross_premium) {
        conditionalColsPresence.gross_premium = true;
      }

      if (row.net_premium) {
        conditionalColsPresence.net_premium = true;
      }

      if (row.sum_insured) {
        conditionalColsPresence.sum_insured = true;
      }

      if (Object.values(conditionalColsPresence).every(Boolean)) {
        break;
      }
    }

    return [
      columnHelper.accessor<typeof getRegionId, ReturnType<typeof getRegionId>>(getRegionId, {
        id: 'region_name',
        header: t('Region'),
        size: defaultColumnSizes.xs,
        enableSorting: false,
        cell: ({getValue}) => <span>{getValue()}</span>,
      }),
      columnHelper.accessor<typeof getCropId, ReturnType<typeof getCropId>>(getCropId, {
        id: 'crop_id',
        header: t('harvest_crop'),
        size: defaultColumnSizes.s,
        enableSorting: false,
        cell: ({getValue}) => (
          <span className="mask">
            <CropTags cropIds={[getValue()]} />
          </span>
        ),
      }),
      columnHelper.accessor<typeof getHarvestYear, ReturnType<typeof getHarvestYear>>(getHarvestYear, {
        id: 'harvest_year',
        header: t('harvest_year'),
        size: defaultColumnSizes.xs,
        enableSorting: false,
        cell: ({getValue}) => <span className="mask">{getValue()}</span>,
      }),
      columnHelper.accessor<typeof getNumSamples, ReturnType<typeof getNumSamples>>(getNumSamples, {
        id: 'num_samples',
        header: t('NumberOfSamples'),
        size: defaultColumnSizes.xs,
        enableSorting: false,
        cell: ({getValue}) => <span className="mask numbers">{getValue()}</span>,
      }),
      columnHelper.accessor<typeof getNumFields, ReturnType<typeof getNumFields>>(getNumFields, {
        id: 'num_fields',
        header: t('NumberOfFields'),
        size: defaultColumnSizes.xs,
        enableSorting: false,
        cell: ({getValue}) => <span className="mask numbers">{getValue()}</span>,
      }),
      columnHelper.accessor<typeof getTotalArea, ReturnType<typeof getTotalArea>>(getTotalArea, {
        id: 'total_area_ha',
        header: t('TotalCultivatedArea'),
        size: defaultColumnSizes.xs,
        enableSorting: false,
        cell: ({getValue}) => (
          <span className="mask numbers">
            <AreaHa value={getValue()} />
          </span>
        ),
      }),
      columnHelper.accessor<typeof getWeightedEstimatedYield, ReturnType<typeof getWeightedEstimatedYield>>(
        getWeightedEstimatedYield,
        {
          id: 'weighted_estimated_yield',
          header: t('EstimatedYieldAreaWeighted'),
          size: defaultColumnSizes.xs,
          enableSorting: false,
          cell: ({getValue, row}) => (
            <span className="mask numbers">
              <YieldTHa value={getValue()} cropId={getCropId(row.original)} />
            </span>
          ),
        },
      ),
      columnHelper.accessor<typeof getStraightEstimatedYield, ReturnType<typeof getStraightEstimatedYield>>(
        getStraightEstimatedYield,
        {
          id: 'straight_estimated_yield',
          header: t('EstimatedYieldArithmeticMean'),
          size: defaultColumnSizes.xs,
          enableSorting: false,
          cell: ({getValue, row}) => (
            <span className="mask numbers">
              <YieldTHa value={getValue()} cropId={getCropId(row.original)} />
            </span>
          ),
        },
      ),
      // Conditional columns
      ...(conditionalColsPresence.gross_premium
        ? [
            columnHelper.accessor<typeof getGrossPremium, ReturnType<typeof getGrossPremium>>(getGrossPremium, {
              id: 'gross_premium',
              header: t('GrossPremium'),
              size: defaultColumnSizes.xs,
              enableSorting: false,
              cell: ({getValue, row}) => (
                <span className="mask numbers">
                  <Currency value={getValue()} />
                </span>
              ),
            }),
          ]
        : []),
      ...(conditionalColsPresence.net_premium
        ? [
            columnHelper.accessor<typeof getNetPremium, ReturnType<typeof getNetPremium>>(getNetPremium, {
              id: 'net_premium',
              header: t('NetPremium'),
              size: defaultColumnSizes.xs,
              enableSorting: false,
              cell: ({getValue, row}) => (
                <span className="mask numbers">
                  <Currency value={getValue()} />
                </span>
              ),
            }),
          ]
        : []),
      ...(conditionalColsPresence.sum_insured
        ? [
            columnHelper.accessor<typeof getSumInsured, ReturnType<typeof getSumInsured>>(getSumInsured, {
              id: 'sum_insured',
              header: t('SumInsured'),
              size: defaultColumnSizes.xs,
              enableSorting: false,
              cell: ({getValue, row}) => (
                <span className="mask numbers">
                  <Currency value={getValue()} />
                </span>
              ),
            }),
          ]
        : []),
    ];
  }, [regionsData, t]);

  return (
    <div className="list-view list-regions">
      <ErrorBoundary>
        <div className="list-view-action-buttons">
          <DownloadButton
            label={'DownloadXLSX'}
            downloadType="region"
            enabled
            params={{
              gadm_level: selectedRegionLevel,
            }}
          />
          <RegionLevelSelector
            selectedRegionLevel={selectedRegionLevel}
            onSelectedRegionLevelChanged={setSelectedRegionLevel}
          />
        </div>
        {isRegionsDataLoading ? (
          <div>
            <SpinningDots size={40} />
          </div>
        ) : columns && regionsData ? (
          <InfinityTable<MapStatsWithRegion, [GadmLevel]>
            columns={columns}
            initialSorting={{id: 'region_name', desc: true}}
            fetchData={fetchDataFromApi}
            fetchSetSize={fetchSetSize}
            tableId="list/Region"
            getRowId={getRowId}
            getSortValue={sortValue}
            additionalQueryKeys={[selectedRegionLevel]}
            noDataWithFiltersMessage={
              selectedRegionLevel
                ? t({
                    type: 'NoDataFoundForYourFiltersAndRegionLevel',
                    regionLevel: `${regionLevelLabels[selectedRegionLevel] ? t(regionLevelLabels[selectedRegionLevel]!) : `${t('RegionLevel')}: ${selectedRegionLevel}`}`,
                  })
                : undefined
            }
          />
        ) : null}
      </ErrorBoundary>
    </div>
  );
};

export default RegionsList;
